import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";
import { AREA_GROUPS } from "@/constants/areaGroups";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * D46 R2 addendum · desk-side combobox.
 * Typing filters live; arrows + Enter pick; last option is
 * "+ create <typed text>" which opens an inline mini-form.
 *
 * Props:
 *  - value: the selected slug/key ("" if none)
 *  - onChange(key, record): fires with the picked/created record
 *  - fetchOptions(q): async (q) => [{ key, label, sub, count }]
 *  - createOption?: { render(closeFn, typed, onCreated), label(typed) }
 *  - placeholder, testId, disabled
 */
export function Combobox({ value, onChange, fetchOptions, createOption, placeholder, testId, disabled, resolveLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [displayLabel, setDisplayLabel] = useState("");
  const anchor = useRef(null);
  const inputRef = useRef(null);

  // Resolve selected key → visible label (organiser name, venue name).
  useEffect(() => {
    let alive = true;
    if (!value) { setDisplayLabel(""); return; }
    if (resolveLabel) {
      Promise.resolve(resolveLabel(value)).then((l) => alive && setDisplayLabel(l || value));
    } else {
      setDisplayLabel(value);
    }
    return () => { alive = false; };
  }, [value, resolveLabel]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const r = await fetchOptions(q);
        setRows(r || []);
        setCursor(0);
      } catch { setRows([]); }
    }, 150);
    return () => clearTimeout(t);
  }, [q, open, fetchOptions]);

  useEffect(() => {
    const onDown = (e) => {
      if (anchor.current && !anchor.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  const total = rows.length + (createOption ? 1 : 0);
  const onKey = (e) => {
    if (!open && ["ArrowDown", "Enter"].includes(e.key)) setOpen(true);
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(total - 1, c + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    if (e.key === "Escape") { setOpen(false); }
    if (e.key === "Enter") {
      e.preventDefault();
      if (cursor < rows.length) { pick(rows[cursor]); return; }
      if (createOption) { setShowCreate(true); }
    }
  };

  const pick = (r) => {
    onChange(r.key, r);
    setDisplayLabel(r.label);
    setOpen(false);
  };

  return (
    <div ref={anchor} className="relative" data-testid={testId}>
      <input
        ref={inputRef}
        value={open ? q : displayLabel}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQ(""); }}
        onKeyDown={onKey}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={`${testId}-input`}
        className="w-full rounded-md border border-neutral-300 p-2 text-sm outline-none focus:border-neutral-900"
      />
      {value && !open && (
        <button type="button" data-testid={`${testId}-clear`}
                onClick={(e) => { e.stopPropagation(); onChange("", null); setDisplayLabel(""); }}
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                         background: "transparent", border: 0, color: "#737373", cursor: "pointer",
                         fontSize: 16, lineHeight: 1 }} aria-label="clear">×</button>
      )}
      {open && (
        <ul data-testid={`${testId}-menu`}
            style={{ position: "absolute", zIndex: 40, top: "calc(100% + 4px)", left: 0, right: 0,
                     maxHeight: 260, overflowY: "auto",
                     background: "#FFFFFF", border: "1px solid #d4d4d4", borderRadius: 8,
                     boxShadow: "0 12px 24px rgba(0,0,0,0.12)" }}>
          {rows.length === 0 && (!createOption || !q.trim()) && (
            <li className="px-3 py-2 text-sm text-neutral-500">no matches</li>
          )}
          {rows.map((r, i) => (
            <li key={r.key}
                data-testid={`${testId}-opt-${r.key}`}
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                onMouseEnter={() => setCursor(i)}
                className="cursor-pointer px-3 py-2 text-sm"
                style={{ background: cursor === i ? "#f4f4f5" : "transparent" }}>
              <div className="font-semibold">{r.label}</div>
              {r.sub && <div className="text-[11px] text-neutral-500">{r.sub}</div>}
              {typeof r.count === "number" && (
                <div className="text-[11px] text-neutral-500">{r.count} event{r.count === 1 ? "" : "s"}</div>
              )}
            </li>
          ))}
          {createOption && q.trim() && (
            <li data-testid={`${testId}-create`}
                onMouseDown={(e) => { e.preventDefault(); setShowCreate(true); }}
                onMouseEnter={() => setCursor(rows.length)}
                className="cursor-pointer border-t border-neutral-100 px-3 py-2 text-sm font-semibold text-neutral-900"
                style={{ background: cursor === rows.length ? "#fff7e6" : "#fffdf5" }}>
              {createOption.label(q.trim())}
            </li>
          )}
        </ul>
      )}
      {showCreate && createOption && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 60,
                       display: "grid", placeItems: "center" }}
             onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div style={{ background: "#FFFFFF", borderRadius: 12, padding: 20, width: "90%", maxWidth: 420 }}
               data-testid={`${testId}-create-modal`}>
            {createOption.render(() => setShowCreate(false), q.trim(), (record) => {
              setShowCreate(false);
              onChange(record.key, record);
              setDisplayLabel(record.label);
              setQ("");
              setOpen(false);
              toast.success("created and selected");
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Organiser combobox -----
export function OrganiserCombobox({ value, onChange, testId = "organiser-combobox" }) {
  const { user } = useAuth();
  const token = localStorage.getItem("session_token");
  const client = axios.create({ baseURL: `${API}/api` });
  const fetchOptions = async (q) => {
    const r = await client.get(`/organisers/autocomplete?q=${encodeURIComponent(q)}`);
    // Count of events per organiser is not returned; keep undefined for now.
    return (r.data.organisers || []).map((o) => ({
      key: o.handle,
      label: (o.name || o.handle).toLowerCase(),
      sub: `@${o.handle}`,
    }));
  };
  const resolveLabel = async (handle) => {
    const r = await client.get(`/organisers/autocomplete?q=${encodeURIComponent(handle)}`);
    const match = (r.data.organisers || []).find((o) => o.handle === handle);
    return match ? (match.name || handle).toLowerCase() + ` · @${handle}` : `@${handle}`;
  };
  return (
    <Combobox
      testId={testId}
      value={value}
      onChange={onChange}
      fetchOptions={fetchOptions}
      resolveLabel={resolveLabel}
      placeholder="search curator or @handle"
      createOption={{
        label: (typed) => `+ create curator "${typed}"`,
        render: (close, typed, done) => (
          <CreateOrganiserForm typed={typed} token={token} onDone={done} onCancel={close} />
        ),
      }}
    />
  );
}

function CreateOrganiserForm({ typed, token, onDone, onCancel }) {
  const client = axios.create({ baseURL: `${API}/api`, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const guessHandle = (typed || "").toLowerCase().replace(/^@+/, "").replace(/[^a-z0-9._-]/g, "");
  const [name, setName] = useState(typed);
  const [handle, setHandle] = useState(guessHandle);
  const [instagram, setInstagram] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!handle.trim()) return toast.error("handle is required");
    setBusy(true);
    try {
      const r = await client.post("/admin/organisers/upsert", {
        handle, name, instagram_url: instagram, website,
      });
      const o = r.data.organiser;
      onDone({ key: o.handle, label: (o.name || o.handle).toLowerCase() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not create");
    } finally { setBusy(false); }
  };
  return (
    <>
      <p className="mb-3 text-sm font-semibold">create curator</p>
      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">name</label>
      <input value={name} onChange={(e) => setName(e.target.value)}
             data-testid="new-org-name"
             className="mb-3 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />
      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">@handle</label>
      <input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/^@+/, ""))}
             data-testid="new-org-handle"
             className="mb-3 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />
      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">instagram (optional)</label>
      <input value={instagram} onChange={(e) => setInstagram(e.target.value)}
             placeholder="https://instagram.com/…"
             data-testid="new-org-instagram"
             className="mb-3 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />
      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">website (optional)</label>
      <input value={website} onChange={(e) => setWebsite(e.target.value)}
             placeholder="https://…"
             data-testid="new-org-website"
             className="mb-4 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel}
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm">cancel</button>
        <button type="button" onClick={save} disabled={busy}
                data-testid="new-org-save"
                style={{ background: "#FFD60A", color: "#1A1A30",
                         fontFamily: "Poppins", fontWeight: 700 }}
                className="rounded-md px-4 py-2 text-sm">
          {busy ? "saving…" : "save & pick"}
        </button>
      </div>
    </>
  );
}

// ----- Venue combobox -----
export function VenueCombobox({ value, onChange, testId = "venue-combobox" }) {
  const token = localStorage.getItem("session_token");
  const client = axios.create({ baseURL: `${API}/api` });
  const [cache, setCache] = useState([]);
  useEffect(() => {
    client.get("/public/venues").then((r) => setCache(r.data.venues || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchOptions = async (q) => {
    const ql = (q || "").toLowerCase().trim();
    return cache
      .filter((v) => !ql || (v.name || "").toLowerCase().includes(ql) || (v.area || "").toLowerCase().includes(ql))
      .slice(0, 30)
      .map((v) => ({ key: v.id, label: (v.name || "").toLowerCase(), sub: v.area ? v.area.toLowerCase() : "", country: v.country || null }));
  };
  const resolveLabel = (id) => {
    const v = cache.find((x) => x.id === id);
    return v ? (v.name || "").toLowerCase() + (v.area ? ` · ${v.area.toLowerCase()}` : "") : "";
  };
  return (
    <Combobox
      testId={testId}
      value={value}
      onChange={onChange}
      fetchOptions={fetchOptions}
      resolveLabel={resolveLabel}
      placeholder="search venue by name or area"
      createOption={{
        label: (typed) => `add "${typed}" as a new venue`,
        render: (close, typed, done) => (
          <CreateVenueForm typed={typed} token={token} onDone={(v) => { setCache((c) => [...c, v]); done({ key: v.id, label: (v.name || "").toLowerCase() }); }} onCancel={close} />
        ),
      }}
    />
  );
}

function CreateVenueForm({ typed, token, onDone, onCancel }) {
  const client = axios.create({ baseURL: `${API}/api`, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const [name, setName] = useState(typed);
  const [area, setArea] = useState("");
  const [googleMaps, setGoogleMaps] = useState("");
  const [directions, setDirections] = useState(""); // Kagz Msg 236 #1
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Msg 234 · flatten side → area options so staff can pick from Vania's list.
  const areaOptions = AREA_GROUPS.flatMap(([side, areas]) => [
    { group: side, value: "", disabled: true, label: side },
    ...areas.map((a) => ({ value: a, label: `  ${a.toLowerCase()}` })),
  ]);

  const save = async () => {
    if (!name.trim()) { setError("name required"); return; }
    if (!area) { setError("pick an area"); return; }
    setBusy(true); setError("");
    try {
      const r = await client.post("/admin/venues/upsert", {
        name: name.trim(),
        area: area || "",
        address: address || "",
        google_maps_url: googleMaps || "",
        directions: directions || "",
        aliases: [],
      });
      const v = r.data.venue;
      if (r.data.matched) {
        toast.success(`matched existing venue · ${v.name}`);
      }
      onDone(v);
    } catch (e) {
      const msg = e?.response?.data?.detail || "could not create";
      setError(String(msg));
      toast.error(String(msg));
    } finally { setBusy(false); }
  };

  return (
    <>
      <p className="mb-1 text-sm font-semibold">add a new venue</p>
      <p className="mb-3 text-[11px] text-neutral-500">
        name and area are enough. maps link, address, and directions are optional.
      </p>
      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">name</label>
      <input value={name} onChange={(e) => setName(e.target.value)}
             data-testid="new-venue-name"
             className="mb-3 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />

      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">area <span className="text-red-600">*</span></label>
      <select value={area} onChange={(e) => setArea(e.target.value)}
              data-testid="new-venue-area"
              className="mb-3 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm bg-white">
        <option value="">pick an area…</option>
        {areaOptions.map((o, i) => o.disabled ? (
          <option key={`g-${i}`} value="" disabled style={{ fontWeight: 700, color: "#525252" }}>{o.label}</option>
        ) : (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">google maps link (optional)</label>
      <input value={googleMaps} onChange={(e) => setGoogleMaps(e.target.value)}
             placeholder="https://maps.google.com/…"
             data-testid="new-venue-gmaps"
             className="mb-3 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />

      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">
        directions (optional) <span className="text-neutral-400 normal-case">— when it's not on maps yet</span>
      </label>
      <textarea value={directions} onChange={(e) => setDirections(e.target.value)}
                rows={2}
                placeholder="behind the total petrol station, second floor…"
                data-testid="new-venue-directions"
                className="mb-3 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />

      <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">address (optional)</label>
      <input value={address} onChange={(e) => setAddress(e.target.value)}
             data-testid="new-venue-address"
             className="mb-2 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />

      {error && (
        <p data-testid="new-venue-error" className="mb-3 text-xs" style={{ color: "#B32020" }}>{error}</p>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel}
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm">cancel</button>
        <button type="button" onClick={save} disabled={busy}
                data-testid="new-venue-save"
                style={{ background: "#FFD60A", color: "#1A1A30",
                         fontFamily: "Poppins", fontWeight: 700 }}
                className="rounded-md px-4 py-2 text-sm">
          {busy ? "saving…" : "save & pick"}
        </button>
      </div>
    </>
  );
}
